<template>
  <ul class="balances-wrapper" v-if="merchants">
    <li v-if="displayMerchantSelect">
      <label class="merchants">
        Merchant
        <vs-select
          autocomplete
          v-model="selectedMerchantId"
          placeholder="Choose merchant">
          <vs-select-item v-for="merc in merchants" :value="merc.id" :key="merc.id" :text="merc.merchant_name" />
        </vs-select>
      </label>
    </li>
    <li>
      <span v-if="showLabels">Balance</span>
      <dl>
        <div v-for="acc in accounts" :class="acc.currency" :key="acc.id">
          <dt>
            {{ acc.currency }}<span>{{ acc.isTest ? 'Test' : 'Real' }}</span>
          </dt>
          <dd>
            <span>{{ acc.amount }}</span>
          </dd>
        </div>
      </dl>
    </li>
  </ul>
</template>
<script>
export default {
  props: {
    displayMerchantSelect: Boolean,
    showLabels: Boolean,
  },
  data() {
    return {
      selectedMerchantId: '',
      merchants: [],
      selectedAccountId: '',
      accounts: [],
    };
  },
  mounted() {
    this.merchants = this.$store.getters.merchantsGetter;
    const currentMerchant = this.$store.getters.storedActiveMerchantGetter
      ? this.$store.getters.storedActiveMerchantGetter
      : this.$store.getters.activeMerchantGetter;
    if (!currentMerchant && !this.$store.getters.activeUserGetter.clientId) {
      // if both are null in vueX, means the user didnt login properly. Logout user
      this.logoutUser();
    }
    this.selectedMerchantId = currentMerchant.id; // not possible to have no merchant
    this.accounts = this.aggregateAccounts(currentMerchant);
    this.$store.commit('UPDATE_ACTIVE_MERCHANT', currentMerchant);
  },
  computed: {
    selectedMerchant() {
      return this.$store.getters.storedActiveMerchantGetter;
    },
  },
  watch: {
    selectedMerchantId(newVal) {
      if (newVal) {
        this.$store.commit('UPDATE_ACTIVE_MERCHANT', this.getMerchant(newVal));
      }
    },
    selectedAccountId(newVal) {
      if (newVal) {
        this.selectedAccount = this.getAccountBalance(newVal);
        this.$store.commit('SET_ACTIVE_ACCOUNT', newVal);
      }
    },
    selectedMerchant(newMerchant) {
      if (!newMerchant) {
        // if no merchants selected, make the first one in the list selected. if no list, return;
        if (!this.merchants || this.merchants.length === 0) {return;}
        this.selectedMerchantId = this.merchants[0].id;
        return;
      }
      this.selectedMerchantId = newMerchant.id;
      this.accounts = this.aggregateAccounts(newMerchant);
      this.selectedAccountId = this.accounts[0].id;
    },
  },
  methods: {
    getMerchant(id) {
      for (let m of this.merchants) {
        if (m.id === id) {
          return m;
        }
      }
    },
    isTestAccount(account) {
      return account.api_id && account.api_id.indexOf('_t') > 0;
    },
    isCustodyAccount(account) {
      return account.settlement_type === 'custody';
    },
    /**
     * Formats all balances for a merchant.
     * grouped by real localCurrency, test localCurrency, real custody and test custody
     * @param merchant
     * @returns {[{'currency', 'amount', 'isTest', 'id'}]}
     */
    aggregateAccounts(merchant) {
      if (!merchant) return;
      // group accounts into one real local currency account, one test local currency account and one account for all custody
      const realLocalCurrencyAccount = merchant.accounts.find(
        (el) => el.crypto_currency === 'BTC'
      ) && {
        currency: merchant.local_currency,
        amount: 0,
        isTest: false,
        id: this.getAccountId('BTC', 'real', merchant, false),
      };
      const testLocalCurrencyAccount = merchant.accounts.find(
        (el) => el.crypto_currency === 'testBTC'
      ) && {
        currency: merchant.local_currency,
        amount: 0,
        isTest: true,
        id: this.getAccountId('testBTC', 'test', merchant, false),
      };
      const res = [];
      if (realLocalCurrencyAccount) {
        res.push(realLocalCurrencyAccount);
      }
      if (testLocalCurrencyAccount) {
        res.push(testLocalCurrencyAccount);
      }
      const custodyAccountsCurrencies =
        this.getCustodyAccountsCurrencies(merchant);
      for (let balance of merchant.balances) {
        if (
          this.isCryptoCurrency(balance.available_currency) &&
          balance.available_currency != 'testBTC'
        ) {
          // check if custody account
          if (
            custodyAccountsCurrencies.indexOf(balance.available_currency) >=
            0 &&
            balance.type === 'real'
          ) {
            res.push({
              currency: balance.available_currency,
              amount: this.formatCryptoAmount(
                balance.available_amount,
                balance.available_currency
              ),
              isTest: false,
              id: this.getAccountId(
                balance.available_currency,
                balance.type,
                merchant,
                true
              ),
            });
          } else if (balance.type === 'test') {
            res.push({
              currency: balance.available_currency,
              amount: this.formatCryptoAmount(
                balance.available_amount,
                balance.available_currency
              ),
              isTest: true,
              id: this.getAccountId(
                balance.available_currency,
                balance.type,
                merchant,
                false
              ),
            });
          }
        } else {
          if (balance.type === 'real') {
            if (realLocalCurrencyAccount) {
              realLocalCurrencyAccount.amount += balance.available_amount;
            }
          } else {
            if (testLocalCurrencyAccount) {
              testLocalCurrencyAccount.amount += balance.available_amount;
            }
          }
        }
      }
      if (realLocalCurrencyAccount) {
        realLocalCurrencyAccount.amount = this.formatFiatPrice(
          realLocalCurrencyAccount.amount
        );
      }
      if (testLocalCurrencyAccount) {
        testLocalCurrencyAccount.amount = this.formatFiatPrice(
          testLocalCurrencyAccount.amount
        );
      }
      return res;
    },
    /**
     * Gets an array of all currencies linked to a custody account
     * @param {*} merchant
     */
    getCustodyAccountsCurrencies(merchant) {
      const res = [];
      merchant.accounts.forEach((account) => {
        if (account.settlement_type === 'custody') {
          res.push(account.crypto_currency);
        }
      });
      return res;
    },
    /**
     * Find the apiId in merchant's accounts, given the currency and type of balance
     * @param {*} currency
     * @param {['real', 'test']} type
     * @param {Merchant} merchant
     * @param {boolean} isCustody
     */
    getAccountId(currency, type, merchant, isCustody) {
      for (let account of merchant.accounts) {
        let isCurrencyMatching = false;
        let isTypeMatching = false;
        let isSettlementMatching = false;
        isTypeMatching =
          (type === 'test' && this.isTestAccount(account)) ||
          (type === 'real' && !this.isTestAccount(account));
        if (account.settlement_type === 'localcurrency') {
          isCurrencyMatching = currency === account.local_currency;
          // special cases : BTC is default active account
          if (currency === 'BTC' || currency === 'testBTC') {
            isCurrencyMatching = currency === account.crypto_currency;
          }
          isSettlementMatching = isCustody ? false : true;
        } else if (account.settlement_type === 'custody') {
          isCurrencyMatching = currency === account.crypto_currency;
          isSettlementMatching = isCustody ? true : false;
        }
        if (isCurrencyMatching && isTypeMatching && isSettlementMatching) {
          return account.api_id;
        }
      }
    },
    /**
     * Fetches the balance linked to the given api id
     * @param {*} id
     */
    getAccountBalance(id) {
      for (let a of this.accounts) {
        if (a.id === id) {
          return a;
        }
      }
      return null;
    },
  },
};
</script>
<style lang="scss">
.balances-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  max-height: 100%;

  &>li:not(:last-of-type) {
    margin-right: 12px;
  }

  &>li>span {
    font-size: 11px;
    position: relative;
    top: 2px;
  }

  label {
    display: flex;
    flex-direction: column;
    font-size: 11px;

    &.merchants {
      .con-select.autocompletex {
        input {
          display: block;
          height: 40px;
          border: none;
          background: #f0f0f0;
          font-size: 14px;
          font-family: 'Montserrat', Helvetica, Arial, sans-serif;

          &:hover {
            cursor: pointer;
            background-color: #e3e3e3;
          }
        }
      }
    }
  }

  dl {
    display: flex;
    flex-wrap: wrap;

    div {
      display: flex;
      height: 100%;
      background-color: #f0f0f0;
      border-radius: 6px;
      padding: 6px;
      margin-right: 6px;
      margin-bottom: 6px;

      // flex-direction: column-reverse;
      &::before {
        content: '';
        background-image: url('../../assets/images/logo/fiat.svg');
        background-repeat: no-repeat;
        background-size: cover;
        display: block;
        height: 24px;
        width: 24px;
        align-self: center;
        margin-right: 6px;
      }

      &.BTC::before {
        background-image: url('../../assets/images/logo/bitcoin.svg');
      }

      &.ETH::before {
        background-image: url('../../assets/images/logo/eth.png');
      }

      &.testBTC::before {
        background-image: url('../../assets/images/logo/testBTC.svg');
      }

      &.LNBC::before {
        background-image: url('../../assets/images/logo/lnbc.png');
      }

      &.USDC::before {
        background-image: url('../../assets/images/logo/usdc.png');
      }

      &.USDT::before {
        background-image: url('../../assets/images/logo/usdt.png');
      }

      dt {
        display: flex;
        flex-direction: column;
        font-weight: bold;
        font-size: 11px;

        span {
          font-weight: normal;
        }
      }

      dd {
        height: 100%;
        line-height: calc(40px - 6px); // total height - paddings
        margin-left: 6px;
        font-weight: bold;

        span {
          display: inline-block;
          vertical-align: middle;
          line-height: normal;
          font-size: 16px;
        }
      }
    }
  }

  &.fiat-payout {
    dl {
      dt {
        justify-content: center;
        margin-right: $margin-s;
        font-weight: normal;
      }
    }
  }
}
</style>
